import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bookmark, CheckCircle2, Loader2, LogOut, User as UserIcon, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import FeedbackHeaderCTA from './FeedbackHeaderCTA';
import AppSidebar, { APP_SIDEBAR_WIDTH } from './AppSidebar';
import CompanyPeoplePanel from './CompanyPeoplePanel';
import JobFeedCard from './JobFeedCard';
import DidYouApplyModal from './DidYouApplyModal';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { buildPanelParamsFromJob, jobApplyKey } from './jobCardUtils';
import { getJobsTracker, setJobStatus, getOrgMatchReasons } from '../api/insights';
import { appDesignTokens as tokens } from './appDesignTokens';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';

const HEADER_HEIGHT = 56;
const TRACKER_PAGE_LIMIT = 10;

const TRACKER_TABS = [
  { id: 'saved', label: 'Saved' },
  { id: 'applied', label: 'Applied' },
  { id: 'rejected', label: 'Rejected' },
];

const EMPTY_STATE_BY_TAB = {
  saved: {
    title: 'No saved jobs yet',
    description: 'Save roles from the Jobs page to shortlist opportunities you want to revisit.',
    icon: Bookmark,
  },
  applied: {
    title: 'No jobs applied yet',
    description: 'Mark roles as applied after submitting your application to track your pipeline.',
    icon: CheckCircle2,
  },
  rejected: {
    title: 'No dismissed jobs yet',
    description: 'Dismiss roles that are not relevant to keep your feed focused and clean.',
    icon: XCircle,
  },
};
const ApplicationsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({ saved: 0, applied: 0, rejected: 0 });
  const [activeTab, setActiveTab] = useState('applied');
  const [meta, setMeta] = useState({});
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedJobKey, setExpandedJobKey] = useState(null);
  const [panelCompany, setPanelCompany] = useState(null);
  const [pendingApplyJob, setPendingApplyJob] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = React.useRef(null);

  useEffect(() => {
    const fn = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);
  const loadTracker = useCallback(async ({ offset = 0, append = false, tab = activeTab } = {}) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError('');
    }
    try {
      const data = await getJobsTracker({
        status: tab,
        offset,
        limit: TRACKER_PAGE_LIMIT,
      });
      const incoming = Array.isArray(data?.jobs) ? data.jobs : [];
      setJobs((prev) => (append ? [...prev, ...incoming] : incoming));
      setCounts(data?.counts || { saved: 0, applied: 0, rejected: 0 });
      setMeta(data?.meta || {});
      setHasMore(!!data?.has_more);
      setNextOffset(Number(data?.next_offset || 0));
    } catch (e) {
      setError(e?.message || 'Failed to load job tracker.');
      if (!append) setJobs([]);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [activeTab]);
  useEffect(() => {
    loadTracker({ offset: 0, append: false, tab: activeTab });
  }, [activeTab, loadTracker]);
  const resolveJobId = (job) => {
    const id = job?.job_id ?? job?.id;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  };
  const tabJobs = useMemo(() => jobs, [jobs]);
  const updateJobInList = useCallback((job, patch) => {
    const jid = resolveJobId(job);
    setJobs((prev) => prev.map((j) => (resolveJobId(j) === jid ? { ...j, ...patch } : j)));
  }, []);
  const removeJobFromList = useCallback((job) => {
    const jid = resolveJobId(job);
    setJobs((prev) => prev.filter((j) => resolveJobId(j) !== jid));
  }, []);
  const moveJobToStatus = useCallback(async (job, status) => {
    const jid = resolveJobId(job);
    if (!jid) return;
    const prevStatus = job.user_status;
    await setJobStatus(jid, status);
    setCounts((prev) => {
      const next = { ...prev };
      if (prevStatus && next[prevStatus] != null) {
        next[prevStatus] = Math.max(0, (next[prevStatus] || 0) - 1);
      }
      next[status] = (next[status] || 0) + 1;
      return next;
    });
    if (status === activeTab) {
      updateJobInList(job, { user_status: status, status_updated_at: new Date().toISOString() });
    } else {
      removeJobFromList(job);
    }
  }, [activeTab, removeJobFromList, updateJobInList]);
  const handleSave = useCallback(async (job) => {
    if (job.user_status === 'saved') return;
    try {
      await moveJobToStatus(job, 'saved');
    } catch (e) {
      setError(e?.message || 'Could not save job.');
    }
  }, [moveJobToStatus]);
  const handleApply = useCallback(async (job) => {
    try {
      await moveJobToStatus(job, 'applied');
    } catch (e) {
      setError(e?.message || 'Could not mark as applied.');
    }
  }, [moveJobToStatus]);

  const handleApplyClick = useCallback((job) => {
    setPendingApplyJob(job);
    const handleFocus = () => {
      setTimeout(() => setShowApplyModal(true), 300);
      window.removeEventListener('focus', handleFocus);
    };
    window.addEventListener('focus', handleFocus);
  }, []);

  const handleApplyConfirm = async (didApply) => {
    if (!pendingApplyJob) return;
    const job = pendingApplyJob;
    setShowApplyModal(false);
    setPendingApplyJob(null);
    if (didApply) {
      await handleApply(job);
    }
  };

  const handleReject = useCallback(async (job) => {
    try {
      await moveJobToStatus(job, 'rejected');
    } catch (e) {
      setError(e?.message || 'Could not dismiss job.');
    }
  }, [moveJobToStatus]);
  const openFindContact = useCallback((job) => {
    setPanelCompany(buildPanelParamsFromJob(job, { initialOuterTab: 'employees', meta }));
  }, [meta]);
  const openViewDetails = useCallback((job) => {
    setPanelCompany(buildPanelParamsFromJob(job, { initialOuterTab: 'jobs', meta }));
  }, [meta]);
  const { isDark } = useTheme();
  const pageBg = isDark ? '#0d1019' : tokens.colors.appShell;
  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : tokens.colors.surface;
  const border = isDark ? 'rgba(255,255,255,0.08)' : tokens.colors.border;
  const text = isDark ? 'rgba(255,255,255,0.9)' : tokens.colors.textPrimary;
  const muted = isDark ? 'rgba(255,255,255,0.45)' : tokens.colors.textMuted;
  const green = isDark ? '#34d399' : tokens.colors.success;
  const headerBg = isDark ? 'rgba(13,16,25,0.92)' : tokens.colors.appShell;
  const menuBg = isDark ? '#0d1019' : tokens.colors.surface;
  const accentColor = isDark ? '#F59E0B' : tokens.colors.accent;
  return (
    <div className="compact-ui" style={{ minHeight: '100vh', background: pageBg, display: 'flex', flexDirection: 'column', fontFamily: tokens.fonts.body }}>
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        height: HEADER_HEIGHT,
        padding: '0 16px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: headerBg,
        borderBottom: `1px solid ${border}`,
        backdropFilter: 'blur(16px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: 'none',
              background: 'transparent',
              color: muted,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
              height: HEADER_HEIGHT,
              padding: '0 16px',
            }}
            aria-label="Back to jobs"
          >
            <ArrowLeft size={15} />
            <span>Back</span>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <div style={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: cardBg,
              borderRadius: tokens.radius.sm,
              padding: 4,
              border: `1px solid ${border}`,
            }}>
              <img src="/assets/logo.png" alt="fairhire logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <span className="app-header-brand" style={{ fontSize: 16, fontWeight: 800, fontFamily: tokens.fonts.heading, color: text }}>fairhire</span>
          </div>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }} ref={profileMenuRef}>
          <ThemeToggle />
          <FeedbackHeaderCTA />
          <button
            type="button"
            onClick={() => setIsProfileMenuOpen((v) => !v)}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.1)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(37,99,235,0.2)'}`,
              fontWeight: 800,
              fontSize: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <UserIcon size={15} color={accentColor} />
          </button>
          <AnimatePresence>
            {isProfileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 8px)',
                  width: 200,
                  background: menuBg,
                  border: `1px solid ${border}`,
                  borderRadius: tokens.radius.card,
                  padding: 8,
                  boxShadow: isDark ? '0 12px 36px rgba(0,0,0,0.6)' : tokens.shadow.menu,
                }}
              >
                <div style={{ padding: '8px 12px', borderBottom: `1px solid ${border}`, marginBottom: 4 }}>
                  <p style={{ fontSize: 11, color: muted, marginBottom: 2 }}>Signed in as</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || user?.email || 'User'}</p>
                </div>
                {[
                  { label: 'Profile', icon: <UserIcon size={14} />, onClick: () => { setIsProfileMenuOpen(false); navigate('/profile'); }, color: isDark ? 'rgba(255,255,255,0.75)' : tokens.colors.textSecondary },
                  { label: 'Logout', icon: <LogOut size={14} />, onClick: () => { logout(); setIsProfileMenuOpen(false); navigate('/'); toast.success('Logged out'); }, color: tokens.colors.danger },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: 'none',
                      border: 'none',
                      color: item.color,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(37,99,235,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                  >
                    {item.icon}{item.label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', marginTop: HEADER_HEIGHT }}>
        <AppSidebar width={APP_SIDEBAR_WIDTH} />
        <main className="applications-main-scroll" style={{ flex: 1, overflow: 'auto', height: `calc(100vh - ${HEADER_HEIGHT}px)` }}>
          <div className="applications-shell-content" style={{ padding: '20px 28px 40px', maxWidth: 940, margin: '0 auto' }}>
            <div className="applications-tabs" style={{
              display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20,
              padding: 4, borderRadius: tokens.radius.pill, background: cardBg, border: `1px solid ${border}`, width: 'fit-content',
              boxShadow: isDark ? 'none' : tokens.shadow.card,
            }}>
              {TRACKER_TABS.map((tab) => {
                const active = activeTab === tab.id;
                const count = counts[tab.id] || 0;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className="applications-tab-button"
                    style={{
                      padding: '8px 16px', borderRadius: 999, border: 'none', cursor: 'pointer',
                      fontSize: 13, fontWeight: 700,
                      background: active ? (isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : tokens.gradients.primaryButton) : 'transparent',
                      color: active ? (isDark ? '#1a1612' : '#fff') : muted,
                      boxShadow: active ? (isDark ? '0 4px 12px rgba(245,158,11,0.25)' : '0 4px 12px rgba(37,99,235,0.2)') : 'none',
                    }}
                  >
                    {tab.label} · {count}
                  </button>
                );
              })}
            </div>
            {loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: muted }}>
                <Loader2 size={18} className="spin" /> Loading…
              </div>
            )}
            {!loading && error && (
              <p style={{ color: tokens.colors.danger, fontSize: 14 }}>{error}</p>
            )}
            {!loading && !error && tabJobs.length === 0 && (() => {
              const cfg = EMPTY_STATE_BY_TAB[activeTab] || EMPTY_STATE_BY_TAB.saved;
              const Icon = cfg.icon;
              return (
                <div
                  style={{
                    margin: '8px auto 0',
                    maxWidth: 760,
                    minHeight: '52vh',
                    border: `1px solid ${border}`,
                    background: cardBg,
                    borderRadius: 14,
                    padding: '28px 20px',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      margin: '0 auto 14px',
                      borderRadius: 14,
                      background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.08)',
                      border: `1px solid ${isDark ? 'rgba(245,158,11,0.2)' : 'rgba(37,99,235,0.14)'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={24} color={accentColor} />
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 30,
                      lineHeight: 1.2,
                      fontWeight: 800,
                      color: text,
                      fontFamily: tokens.fonts.heading,
                    }}
                  >
                    {cfg.title}
                  </h3>
                  <p
                    style={{
                      margin: '14px auto 0',
                      maxWidth: 500,
                      color: muted,
                      fontSize: 15,
                      lineHeight: 1.6,
                      fontWeight: 500,
                    }}
                  >
                    {cfg.description}
                  </p>
                </div>
              );
            })()}
            {!loading && tabJobs.map((job) => {
              const key = jobApplyKey(job) || `${job.company_name}-${job.role}`;
              const isExpanded = expandedJobKey === key;
              return (
                <JobFeedCard
                  key={key}
                  job={job}
                  isExpanded={isExpanded}
                  onToggleExpand={async (k) => {
                    const willExpand = !isExpanded;
                    setExpandedJobKey(willExpand ? k : null);
                    if (willExpand && !job.reasons && job.org_id) {
                      try {
                        const data = await getOrgMatchReasons(job.org_id);
                        updateJobInList(job, { reasons: data.reasons || [] });
                      } catch (e) {
                        console.error("Failed to fetch reasons", e);
                      }
                    }
                  }}
                  onToggleSaved={handleSave}
                  onReject={handleReject}
                  onApply={handleApplyClick}
                  onFindContact={() => openFindContact(job)}
                  onViewDetails={() => openViewDetails(job)}
                  showSave={job.user_status !== 'saved'}
                  showDismiss={job.user_status !== 'rejected'}
                  cardBg={cardBg}
                  border={border}
                  text={text}
                  muted={muted}
                  green={green}
                />
              );
            })}
            {!loading && !error && hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
                <button
                  type="button"
                  disabled={loadingMore}
                  onClick={() => loadTracker({ offset: nextOffset, append: true, tab: activeTab })}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: `1px solid ${border}`,
                    background: cardBg,
                    color: isDark ? 'rgba(255,255,255,0.65)' : tokens.colors.textSecondary,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                    opacity: loadingMore ? 0.7 : 1,
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
      <CompanyPeoplePanel
        isOpen={panelCompany !== null}
        onClose={() => setPanelCompany(null)}
        sourceOrgId={panelCompany?.sourceOrgId}
        sourceCompanyName={panelCompany?.sourceCompanyName}
        destOrgId={panelCompany?.destOrgId}
        companyName={panelCompany?.companyName}
        initialHop={panelCompany?.hop || 1}
        role={panelCompany?.role || ''}
        location={panelCompany?.location || ''}
        initialOuterTab={panelCompany?.initialOuterTab || 'employees'}
        initialSelectedJobId={panelCompany?.initialSelectedJobId}
        initialSelectedJob={panelCompany?.initialSelectedJob}
        isFromJobsPage={panelCompany?.isFromJobsPage}
      />
      <DidYouApplyModal
        isOpen={showApplyModal}
        onClose={() => {
          setShowApplyModal(false);
          setPendingApplyJob(null);
        }}
        onConfirm={handleApplyConfirm}
        jobTitle={pendingApplyJob?.role}
        companyName={pendingApplyJob?.companyName}
      />
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (max-width: 720px), (max-device-width: 720px) {
          .app-header-brand { display: none; }
          .applications-main-scroll {
            width: 100% !important;
            height: calc(100vh - ${HEADER_HEIGHT}px - 74px - env(safe-area-inset-bottom)) !important;
            padding-bottom: calc(74px + env(safe-area-inset-bottom)) !important;
          }
          .applications-shell-content { padding: 16px 12px 96px !important; }
          .applications-tabs {
            width: 100% !important;
            display: grid !important;
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            gap: 4px !important;
            margin-bottom: 14px !important;
          }
          .applications-tab-button {
            min-width: 0 !important;
            padding: 9px 8px !important;
            font-size: 12px !important;
            white-space: nowrap;
          }
        }
      `}</style>
    </div>
  );
};
export default ApplicationsPage;
